import type React from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { useIsWeekToModuleEnabledForCourse } from 'bundles/custom-labels/utils/featureFlags';
import type {
  CustomLabelsV2,
  CustomLabelsV2ByUserAndCourseQuery,
  CustomLabelsV2ByUserAndCourseQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/CustomLabelsV2';

import type { ReplaceCustomContent } from '../types/CustomLabels';
import withCustomLabelsMapping, { useWithCustomLabelsMapping } from './withCustomLabelsMapping';

type OuterProps = {
  courseId: string;
  loadingComponent?: React.ReactNode;
};

type UserProps = {
  userId: string;
};

type InnerProps = OuterProps &
  UserProps & {
    customLabelsV2?: CustomLabelsV2;
    replaceCustomContent: ReplaceCustomContent;
  };

/* eslint-disable graphql/template-strings */
export const CustomLabelsQuery = gql`
  query CustomLabelsQuery($userId: String!, $courseId: String!) {
    CustomLabelsV2 @naptime {
      byUserAndCourse(userId: $userId, courseId: $courseId) {
        elements {
          id
          moduleNames
          isRhyme
          namings {
            courseraCourse
            courseraWeek
          }
        }
      }
    }
  }
`;
/* eslint-enable graphql/template-strings */

type CustomLabelsByUserAndCourse = {
  isLoading: boolean;
  replaceCustomContent: ReplaceCustomContent;
};

/**
 * Custom hook to replace `withCustomLabelsByUserAndCourse` HOC. The original HOC returns `null`
 * while GraphQL query is loading. For further flexibility, it returns `isLoading` to allow
 * consumer more user-friendly rendering if desired.
 * @param object Contains `courseId: string` prop
 * @returns Object with `isLoading: boolean` and `replaceCustomContent(string) => string`
 */
export const useCustomLabelsByUserAndCourse = ({ courseId }: OuterProps): CustomLabelsByUserAndCourse => {
  const userId = user.get().id.toString();

  const queryResult = useQuery<CustomLabelsV2ByUserAndCourseQuery, CustomLabelsV2ByUserAndCourseQueryVariables>(
    CustomLabelsQuery,
    { variables: { courseId, userId }, ssr: false }
  );

  const customLabelsV2 = queryResult?.data?.CustomLabelsV2?.byUserAndCourse?.elements?.[0];

  const isWeekToModuleEnabledForCourse = useIsWeekToModuleEnabledForCourse(courseId);
  const customLabelsMapping = useWithCustomLabelsMapping({ customLabelsV2, isWeekToModuleEnabledForCourse });

  return {
    isLoading: queryResult.loading,
    ...customLabelsMapping,
  };
};

export default <PropsFromCaller extends OuterProps>(
  Component: React.ComponentType<PropsFromCaller & InnerProps>,
  loadingComponent?: React.ReactNode
) =>
  compose<InnerProps & PropsFromCaller, PropsFromCaller>(
    withProps<UserProps, PropsFromCaller>(() => ({
      userId: user.get().id.toString(),
    })),
    waitForGraphQL<
      PropsFromCaller & InnerProps,
      CustomLabelsV2ByUserAndCourseQuery,
      CustomLabelsV2ByUserAndCourseQueryVariables,
      InnerProps
    >(
      CustomLabelsQuery,
      {
        options: ({ courseId, userId }) => ({
          variables: {
            courseId,
            userId,
          },
          ssr: false,
        }),
        skip: ({ courseId, userId }) => !courseId || !userId,
        props: ({ data, ownProps }) => {
          const customLabelsV2 = data?.CustomLabelsV2?.byUserAndCourse?.elements?.[0];

          return {
            ...ownProps,
            customLabelsV2,
          };
        },
      },
      loadingComponent
    ),
    withCustomLabelsMapping
  )(Component);
